import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LuPencil, LuLock, LuCircleHelp, LuMessageSquare,
  LuChevronRight, LuChevronDown, LuBell, LuMail, LuArrowLeft,
} from 'react-icons/lu';
import TransporterLayout from '../../components/TransporterLayout/TransporterLayout';
import AddTruckDrawer from '../../components/AddTruckDrawer/AddTruckDrawer';
import AddDriverDrawer from '../../components/AddDriverDrawer/AddDriverDrawer';
import {
  fetchCurrentUser, updateCurrentUser, deleteDirectusUser,
  fetchMyPartner,
  fetchTrucks, createTruck, updateTruck, deleteTruck,
  fetchDrivers, createDriver, updateDriver, deleteDriver, createDirectusUser, fetchRoleId,
  getAssetUrl,
} from '../../lib/directus';
import styles from './TransporterProfile.module.css';

export default function TransporterProfile() {
  const navigate = useNavigate();
  const [user, setUser]       = useState(null);
  const [partner, setPartner] = useState(null);
  const [trucks, setTrucks]   = useState([]);
  const [drivers, setDrivers] = useState([]);
  const [loading, setLoading] = useState(true);

  // Edit profile
  const [editMode, setEditMode]   = useState(false);
  const [editFirst, setEditFirst] = useState('');
  const [editLast, setEditLast]   = useState('');
  const [editEmail, setEditEmail] = useState('');
  const [saving, setSaving]       = useState(false);

  // Change password
  const [pwOpen, setPwOpen]         = useState(false);
  const [currentPw, setCurrentPw]   = useState('');
  const [newPw, setNewPw]           = useState('');
  const [confirmPw, setConfirmPw]   = useState('');
  const [pwError, setPwError]       = useState('');
  const [pwSuccess, setPwSuccess]   = useState(false);
  const [pwSaving, setPwSaving]     = useState(false);

  // Preferences — persisted in localStorage, also pushed to API
  const [pushNotif, setPushNotif]   = useState(() => localStorage.getItem('pref_push') !== 'false');
  const [emailNotif, setEmailNotif] = useState(() => localStorage.getItem('pref_email') === 'true');

  // Drawers
  const [addTruckOpen, setAddTruckOpen]   = useState(false);
  const [addDriverOpen, setAddDriverOpen] = useState(false);
  const [editingTruck, setEditingTruck]   = useState(null);
  const [editingDriver, setEditingDriver] = useState(null);
  const [deletingKey, setDeletingKey]     = useState('');

  const loadData = useCallback(() => {
    setLoading(true);
    Promise.allSettled([
      fetchCurrentUser(),
      fetchMyPartner(),
    ]).then(async ([userRes, partnerRes]) => {
      if (userRes.status === 'fulfilled') setUser(userRes.value);

      const partnerRecord = partnerRes.status === 'fulfilled' ? partnerRes.value : null;
      if (partnerRecord) setPartner(partnerRecord);

      const [trucksRes, driversRes] = await Promise.allSettled([
        fetchTrucks(partnerRecord?.id || null),
        fetchDrivers(partnerRecord?.id || null),
      ]);

      if (trucksRes.status === 'fulfilled')  setTrucks(trucksRes.value);
      if (driversRes.status === 'fulfilled') setDrivers(driversRes.value);
      setLoading(false);
    });
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  /* ── Edit profile ── */
  function startEdit() {
    setEditFirst(user?.first_name || '');
    setEditLast(user?.last_name || '');
    setEditEmail(user?.email || '');
    setEditMode(true);
  }

  async function handleSaveProfile() {
    setSaving(true);
    try {
      const updated = await updateCurrentUser({
        first_name: editFirst.trim(),
        last_name:  editLast.trim(),
        email:      editEmail.trim(),
      });
      setUser(prev => ({ ...prev, ...updated }));
      setEditMode(false);
    } catch (err) {
      console.error(err);
    } finally {
      setSaving(false);
    }
  }

  /* ── Change password ── */
  async function handleChangePassword() {
    if (!newPw)              { setPwError('Enter a new password'); return; }
    if (newPw !== confirmPw) { setPwError('Passwords do not match'); return; }
    if (newPw.length < 8)    { setPwError('Password must be at least 8 characters'); return; }
    setPwError('');
    setPwSaving(true);
    try {
      await updateCurrentUser({ password: newPw });
      setPwSuccess(true);
      setCurrentPw(''); setNewPw(''); setConfirmPw('');
      setTimeout(() => { setPwSuccess(false); setPwOpen(false); }, 2000);
    } catch (err) {
      setPwError(err.message || 'Failed to update password');
    } finally {
      setPwSaving(false);
    }
  }

  /* ── Preferences ── */
  function handlePushToggle(val) {
    setPushNotif(val);
    localStorage.setItem('pref_push', String(val));
    updateCurrentUser({ push_notifications: val }).catch(() => {});
  }

  function handleEmailToggle(val) {
    setEmailNotif(val);
    localStorage.setItem('pref_email', String(val));
    updateCurrentUser({ email_notifications: val }).catch(() => {});
  }

  /* ── Add truck / driver ── */
  async function handleAddTruck(data) {
    const payload = { ...data };
    if (partner?.id) payload.partner = partner.id;
    await createTruck(payload);
    setAddTruckOpen(false);
    fetchTrucks(partner?.id || null).then(setTrucks).catch(console.error);
  }

  async function handleEditTruck(data) {
    if (!editingTruck?.id) return;
    const payload = { ...data };
    if (partner?.id) payload.partner = partner.id;
    await updateTruck(editingTruck.id, payload);
    setEditingTruck(null);
    fetchTrucks(partner?.id || null).then(setTrucks).catch(console.error);
    fetchDrivers(partner?.id || null).then(setDrivers).catch(console.error);
  }

  async function handleAddDriver(data) {
    const nameParts  = (data.name || '').trim().split(' ');
    const driverRole = await fetchRoleId('Driver');
    const directusUser = await createDirectusUser({
      first_name: nameParts[0] || data.name,
      last_name:  nameParts.slice(1).join(' ') || '',
      email:      data.email,
      password:   data.password,
      role:       driverRole,
    });
    const driverPayload = { name: data.name, email: data.email, phone: data.phone || null, user: directusUser.id };
    if (partner?.id) driverPayload.partner = partner.id;
    await createDriver(driverPayload);
    setAddDriverOpen(false);
    fetchDrivers(partner?.id || null).then(setDrivers).catch(console.error);
  }

  async function handleEditDriver(data) {
    if (!editingDriver?.id) return;
    const payload = {
      name: data.name,
      email: data.email,
      phone: data.phone || null,
    };
    await updateDriver(editingDriver.id, payload);
    setEditingDriver(null);
    fetchDrivers(partner?.id || null).then(setDrivers).catch(console.error);
    fetchTrucks(partner?.id || null).then(setTrucks).catch(console.error);
  }

  async function handleDeleteTruck(truck) {
    const label = truck.truck_id || `Truck #${String(truck.id).slice(-4).toUpperCase()}`;
    if (!window.confirm(`Delete ${label}?`)) return;
    setDeletingKey(`truck:${truck.id}`);
    try {
      await deleteTruck(truck.id);
      fetchTrucks(partner?.id || null).then(setTrucks).catch(console.error);
      fetchDrivers(partner?.id || null).then(setDrivers).catch(console.error);
    } catch (err) {
      window.alert(err.message || 'Failed to delete truck');
    } finally {
      setDeletingKey('');
    }
  }

  async function handleDeleteDriver(driver) {
    if (!window.confirm(`Delete driver ${driver.name || 'record'}?`)) return;
    setDeletingKey(`driver:${driver.id}`);
    const linkedUserId = typeof driver.user === 'string' ? driver.user : driver.user?.id || null;
    let deletedDriverRecord = false;
    try {
      await deleteDriver(driver.id);
      deletedDriverRecord = true;
      if (!linkedUserId) {
        throw new Error('Driver record deleted, but no linked Directus user was found to remove.');
      }
      await deleteDirectusUser(linkedUserId);
      fetchDrivers(partner?.id || null).then(setDrivers).catch(console.error);
      fetchTrucks(partner?.id || null).then(setTrucks).catch(console.error);
    } catch (err) {
      window.alert(err.message || 'Failed to delete driver');
    } finally {
      if (deletedDriverRecord) {
        fetchDrivers(partner?.id || null).then(setDrivers).catch(console.error);
        fetchTrucks(partner?.id || null).then(setTrucks).catch(console.error);
      }
      setDeletingKey('');
    }
  }

  /* ── Derived display values ── */
  const displayName = user
    ? [user.first_name, user.last_name].filter(Boolean).join(' ') || '—'
    : '—';
  const avatarSrc = user?.avatar ? getAssetUrl(user.avatar) : '/avatar-holder-image.jpg';

  const driverOptions = drivers.map(d => ({ id: d.id, name: d.name || '—' }));
  return (
    <TransporterLayout>
      <div className={styles.page}>
        <button className={styles.backBtn} onClick={() => navigate(-1)}>
          <LuArrowLeft size={16} />
          Back
        </button>
        <div className={styles.topRow}>

          {/* ── Profile card ── */}
          <div className={styles.profileCard}>
            <img src={avatarSrc} alt="Avatar" className={styles.avatarImg} />
            {editMode ? (
              <>
                <input
                  className={styles.profileInput}
                  placeholder="First name"
                  value={editFirst}
                  onChange={e => setEditFirst(e.target.value)}
                />
                <input
                  className={styles.profileInput}
                  placeholder="Last name"
                  value={editLast}
                  onChange={e => setEditLast(e.target.value)}
                />
                <input
                  className={styles.profileInput}
                  placeholder="Email"
                  type="email"
                  value={editEmail}
                  onChange={e => setEditEmail(e.target.value)}
                />
                <div className={styles.profileEditActions}>
                  <button className={styles.btnSave} onClick={handleSaveProfile} disabled={saving}>
                    {saving ? 'Saving…' : 'Save'}
                  </button>
                  <button className={styles.btnCancel} onClick={() => setEditMode(false)}>
                    Cancel
                  </button>
                </div>
              </>
            ) : (
              <>
                <p className={styles.profileName}>{loading ? '—' : displayName}</p>
                <p className={styles.profileEmail}>{loading ? '—' : (user?.email || '—')}</p>
                <button className={styles.editBtn} onClick={startEdit}>
                  <LuPencil size={13} />
                  Edit profile
                </button>
              </>
            )}
          </div>

          {/* ── Settings card ── */}
          <div className={styles.settingsCard}>
            <p className={styles.settingsSectionTitle}>Personal settings</p>

            <div className={styles.settingsList}>
              {/* Change password */}
              <div>
                <div
                  className={styles.settingsRow}
                  onClick={() => { setPwOpen(o => !o); setPwError(''); setPwSuccess(false); }}
                >
                  <div className={styles.settingsRowLeft}>
                    <LuLock size={16} color="#6b7280" />
                    <span className={styles.settingsRowText}>Change password</span>
                  </div>
                  {pwOpen
                    ? <LuChevronDown size={16} color="#9ca3af" />
                    : <LuChevronRight size={16} color="#9ca3af" />}
                </div>
                {pwOpen && (
                  <div className={styles.changePwSection}>
                    <input
                      type="password"
                      className={styles.changePwInput}
                      placeholder="Current password"
                      value={currentPw}
                      onChange={e => setCurrentPw(e.target.value)}
                    />
                    <input
                      type="password"
                      className={styles.changePwInput}
                      placeholder="New password"
                      value={newPw}
                      onChange={e => setNewPw(e.target.value)}
                    />
                    <input
                      type="password"
                      className={styles.changePwInput}
                      placeholder="Confirm new password"
                      value={confirmPw}
                      onChange={e => setConfirmPw(e.target.value)}
                    />
                    {pwError   && <p className={styles.msgError}>{pwError}</p>}
                    {pwSuccess && <p className={styles.msgSuccess}>Password updated successfully!</p>}
                    <div className={styles.changePwActions}>
                      <button
                        className={styles.btnSave}
                        style={{ flex: 'none', padding: '7px 20px' }}
                        onClick={handleChangePassword}
                        disabled={pwSaving}
                      >
                        {pwSaving ? 'Saving…' : 'Save changes'}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              {/* Help center */}
              <div
                className={styles.settingsRow}
                onClick={() => window.open('https://driftwoodagri.com/help', '_blank')}
              >
                <div className={styles.settingsRowLeft}>
                  <LuCircleHelp size={16} color="#6b7280" />
                  <span className={styles.settingsRowText}>Help center</span>
                </div>
                <LuChevronRight size={16} color="#9ca3af" />
              </div>

              {/* Send us a message */}
              <div
                className={styles.settingsRow}
                style={{ borderBottom: 'none' }}
                onClick={() => { window.location.href = 'mailto:support@driftwoodagri.com'; }}
              >
                <div className={styles.settingsRowLeft}>
                  <LuMessageSquare size={16} color="#6b7280" />
                  <span className={styles.settingsRowText}>Send us a message</span>
                </div>
                <LuChevronRight size={16} color="#9ca3af" />
              </div>
            </div>

            <p className={styles.settingsSectionTitle} style={{ marginTop: 20 }}>Preferences</p>

            <div className={styles.settingsList}>
              <div className={styles.settingsRow}>
                <div className={styles.settingsRowLeft}>
                  <LuBell size={16} color="#6b7280" />
                  <span className={styles.settingsRowText}>Push notifications</span>
                </div>
                <label className={styles.toggle} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className={styles.toggleInput}
                    checked={pushNotif}
                    onChange={e => handlePushToggle(e.target.checked)}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
              <div className={styles.settingsRow} style={{ borderBottom: 'none' }}>
                <div className={styles.settingsRowLeft}>
                  <LuMail size={16} color="#6b7280" />
                  <span className={styles.settingsRowText}>Email notification</span>
                </div>
                <label className={styles.toggle} onClick={e => e.stopPropagation()}>
                  <input
                    type="checkbox"
                    className={styles.toggleInput}
                    checked={emailNotif}
                    onChange={e => handleEmailToggle(e.target.checked)}
                  />
                  <span className={styles.toggleSlider} />
                </label>
              </div>
            </div>
          </div>
        </div>

        {/* ── Trucks ── */}
        <div className={styles.tableCard} style={{ marginTop: 24 }}>
          <div className={styles.tableHeader}>
            <p className={styles.tableTitle}>Trucks</p>
            <button className={styles.addNewBtn} onClick={() => setAddTruckOpen(true)}>+ Add new</button>
          </div>
          <div className={styles.tableHead}>
            <span className={styles.tableHeadCell} style={{ flex: 2 }}>ID</span>
            <span className={styles.tableHeadCell} style={{ flex: 1 }}>Capacity</span>
            <span className={styles.tableHeadCell} style={{ flex: 1 }}>Assigned driver</span>
            <span className={styles.tableHeadCell} style={{ flex: 0.5 }}></span>
          </div>
          {loading && <div className={styles.loadingRow}>Loading...</div>}
          {!loading && trucks.length === 0 && <div className={styles.loadingRow}>No trucks added yet.</div>}
          {!loading && trucks.map(truck => {
            const label    = truck.truck_id || `Truck #${String(truck.id).slice(-4).toUpperCase()}`;
            const sub      = [truck.make, truck.license_plate].filter(Boolean).join(' • ');
            const capacity = truck.capacity != null ? `${truck.capacity} MT` : '—';
            const driver   = truck.driver?.name || '—';
            return (
              <div key={truck.id} className={styles.tableRow}>
                <div style={{ flex: 2 }}>
                  <p className={styles.tableRowName}>{label}</p>
                  {sub && <p className={styles.tableRowSub}>{sub}</p>}
                </div>
                <span className={styles.tableRowValue} style={{ flex: 1 }}>{capacity}</span>
                <span className={styles.tableRowValue} style={{ flex: 1 }}>{driver}</span>
                <div className={styles.rowActions} style={{ flex: 0.5 }}>
                  <button
                    className={styles.editRowBtn}
                    onClick={() => setEditingTruck(truck)}
                    disabled={deletingKey === `truck:${truck.id}`}
                  >
                    Edit
                  </button>
                  <button
                    className={styles.deleteRowBtn}
                    onClick={() => handleDeleteTruck(truck)}
                    disabled={deletingKey === `truck:${truck.id}`}
                  >
                    {deletingKey === `truck:${truck.id}` ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>

        {/* ── Drivers ── */}
        <div className={styles.tableCard} style={{ marginTop: 24 }}>
          <div className={styles.tableHeader}>
            <p className={styles.tableTitle}>Drivers</p>
            <button className={styles.addNewBtn} onClick={() => setAddDriverOpen(true)}>+ Add new</button>
          </div>
          <div className={styles.tableHead}>
            <span className={styles.tableHeadCell} style={{ flex: 2 }}>Name</span>
            <span className={styles.tableHeadCell} style={{ flex: 1 }}>Assigned truck</span>
            <span className={styles.tableHeadCell} style={{ flex: 1 }}>Phone</span>
            <span className={styles.tableHeadCell} style={{ flex: 0.5 }}></span>
          </div>
          {loading && <div className={styles.loadingRow}>Loading...</div>}
          {!loading && drivers.length === 0 && <div className={styles.loadingRow}>No drivers added yet.</div>}
          {!loading && drivers.map(driver => {
            const truckLabel = driver.truck?.truck_id
              || (driver.truck?.id ? `Truck #${String(driver.truck.id).slice(-4).toUpperCase()}` : '—');
            return (
              <div key={driver.id} className={styles.tableRow}>
                <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 10 }}>
                  <div className={styles.driverAvatar}>{(driver.name || '?').charAt(0)}</div>
                  <div>
                    <p className={styles.tableRowName}>{driver.name || '—'}</p>
                    <p className={styles.tableRowSub}>{driver.email || ''}</p>
                  </div>
                </div>
                <span className={styles.tableRowValue} style={{ flex: 1 }}>{truckLabel}</span>
                <span className={styles.tableRowOlive} style={{ flex: 1 }}>{driver.phone || '—'}</span>
                <div className={styles.rowActions} style={{ flex: 0.5 }}>
                  <button
                    className={styles.editRowBtn}
                    onClick={() => setEditingDriver(driver)}
                    disabled={deletingKey === `driver:${driver.id}`}
                  >
                    Edit
                  </button>
                  <button
                    className={styles.deleteRowBtn}
                    onClick={() => handleDeleteDriver(driver)}
                    disabled={deletingKey === `driver:${driver.id}`}
                  >
                    {deletingKey === `driver:${driver.id}` ? 'Deleting…' : 'Delete'}
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      <AddTruckDrawer
        isOpen={addTruckOpen}
        onClose={() => setAddTruckOpen(false)}
        drivers={driverOptions}
        onAdd={handleAddTruck}
      />
      <AddTruckDrawer
        isOpen={!!editingTruck}
        onClose={() => setEditingTruck(null)}
        drivers={driverOptions}
        onAdd={handleEditTruck}
        mode="edit"
        initialData={editingTruck}
      />
      <AddDriverDrawer
        isOpen={addDriverOpen}
        onClose={() => setAddDriverOpen(false)}
        onAdd={handleAddDriver}
      />
      <AddDriverDrawer
        isOpen={!!editingDriver}
        onClose={() => setEditingDriver(null)}
        onAdd={handleEditDriver}
        mode="edit"
        initialData={editingDriver}
      />
    </TransporterLayout>
  );
}
